import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip,
  CartesianGrid, Cell, PieChart, Pie, Legend,
} from "recharts";
import {
  DollarSign, FileText, Receipt, Banknote, ClipboardList, TrendingUp,
  Activity, Building2, ChevronRight, Clock, AlertTriangle, CheckCircle2,
  TrendingDown, ArrowUpRight, LayoutGrid, ArrowLeft, Send, Paperclip, Ban, Tag,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, KPI, Button } from "@/components/ui";
import { format, parseISO, formatDistanceToNow } from "date-fns";
import { isPresenting, MONEY_MASK } from "@/lib/presenting";

// =============================================================================
// FinanceHubScreen — /finance (T-075 — Finance hub command centre)
//
// Director/manager-only commercial dashboard. KPIs across the money pipeline,
// top-clients-by-value chart with margin overlay, recent activity feed.
//
// Mirrors the ProjectHubScreen treatment per James 2026-04-25:
//   "nice clean widget to maybe show client value vs margin or some other
//    cool commercial metrics that make the portal feel more rounded out"
// =============================================================================

// T-508 — this screen rolls its own compact formatter ($1.2M / $340k) rather
// than using lib/dates. It has to honour Presenting mode as well, or the
// Finance hub would be the one place the toggle did nothing.
function fmtMoney(n: number | null | undefined): string {
  if (n == null) return "—";
  if (isPresenting()) return MONEY_MASK;
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000)     return `$${(n / 1_000).toFixed(0)}k`;
  return `$${n.toFixed(0)}`;
}

interface ProjectMargin {
  project_id:        string;
  project_code:      string;
  project_name:      string;
  status:            string;
  contract_value:    number | null;
  spend_to_date:     number | null;
  gross_margin:      number | null;
  margin_pct:        number | null;
  invoiced_to_date:  number | null;
  paid_to_date:      number | null;
}

interface ProjectWithClient extends ProjectMargin {
  client?: { client_id: string; legal_name: string } | null;
}

// -----------------------------------------------------------------------------

export default function FinanceHubScreen() {
  const user = useRequireAuth();
  const canManage = hasRole(user, "manager");

  // -- Per-project margin + client (for KPIs + chart) --------------------
  const { data: projects = [] } = useQuery({
    queryKey: ["finance_hub_projects"],
    queryFn: async () => {
      const { data: marginRows } = await supabase
        .from("v_project_margin")
        .select("*");

      const projectIds = (marginRows ?? []).map((r: any) => r.project_id);
      let clientMap = new Map<string, { client_id: string; legal_name: string }>();
      if (projectIds.length > 0) {
        const { data: projClients } = await supabase
          .from("projects")
          .select("project_id, client:client_id(client_id, legal_name)")
          .in("project_id", projectIds);
        for (const p of (projClients ?? []) as any[]) {
          if (p.client) clientMap.set(p.project_id, p.client);
        }
      }

      return ((marginRows ?? []) as ProjectMargin[]).map(p => ({
        ...p,
        client: clientMap.get(p.project_id) ?? null,
      })) as ProjectWithClient[];
    },
  });

  // -- KPIs --------------------------------------------------------------
  const kpis = useMemo(() => {
    const ACTIVE = ["mobilising", "live", "acceptance_pending"];
    const active = projects.filter(p => ACTIVE.includes(p.status));

    const arOutstanding =
      projects.reduce((s, p) => s + ((p.invoiced_to_date ?? 0) - (p.paid_to_date ?? 0)), 0);
    const activeContractValue = active.reduce((s, p) => s + (p.contract_value ?? 0), 0);
    const totalGrossMargin = active.reduce((s, p) => s + (p.gross_margin ?? 0), 0);
    const avgMarginPct = active.length > 0
      ? active.reduce((s, p) => s + (p.margin_pct ?? 0), 0) / active.length
      : 0;

    return {
      activeContractValue,
      totalGrossMargin,
      avgMarginPct,
      arOutstanding,
      activeCount: active.length,
    };
  }, [projects]);

  // -- Quote pipeline + AP backlog (separate queries) --------------------
  const { data: quoteStats } = useQuery({
    queryKey: ["finance_hub_quotes"],
    queryFn: async () => {
      const { data } = await supabase
        .from("quotes")
        .select("quote_id, total_incl_gst, status")
        .in("status", ["sent", "pending_approval"]);
      const total = (data ?? []).reduce((s, q: any) => s + (q.total_incl_gst ?? 0), 0);
      return { count: data?.length ?? 0, total };
    },
  });

  const { data: apStats } = useQuery({
    queryKey: ["finance_hub_ap"],
    queryFn: async () => {
      const { data } = await supabase
        .from("ap_invoices")
        .select("ap_invoice_id, amount_inc_gst, status")   // T-397 spelling fix
        .in("status", ["captured", "pending_review", "approved"]);
      const total = (data ?? []).reduce((s, a: any) => s + Number(a.amount_inc_gst ?? 0), 0);
      return { count: data?.length ?? 0, total };
    },
  });

  // -- Top clients by contract value (for chart) -------------------------
  const topClients = useMemo(() => {
    const byClient = new Map<string, { name: string; contract: number; margin: number; count: number; marginPct: number }>();
    for (const p of projects) {
      if (!p.client) continue;
      const existing = byClient.get(p.client.client_id) ?? {
        name: p.client.legal_name,
        contract: 0, margin: 0, count: 0, marginPct: 0,
      };
      existing.contract += p.contract_value ?? 0;
      existing.margin   += p.gross_margin ?? 0;
      existing.count    += 1;
      byClient.set(p.client.client_id, existing);
    }
    // Compute weighted margin %
    const arr = Array.from(byClient.values()).map(c => ({
      ...c,
      marginPct: c.contract > 0 ? (c.margin / c.contract) * 100 : 0,
    }));
    return arr.sort((a, b) => b.contract - a.contract).slice(0, 6);
  }, [projects]);

  if (!user) return null;

  // Container — full viewport width, see ProjectHub for rationale.
  return (
    <div className="px-6 md:px-10 py-6 md:py-8 pb-20">
      <Link to="/" className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-accent mb-3">
        <ArrowLeft size={14}/> Home
      </Link>
      {/* Header */}
      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-3 mb-1">
            <div className="h-1 w-10 bg-accent rounded-full" />
            <div className="text-text-muted text-[11px] tracking-wider2 uppercase font-mono">Finance portal</div>
          </div>
          <h1 className="text-[24px] md:text-[28px] font-semibold tracking-tight flex items-center gap-2">
            <DollarSign size={24} className="text-accent" />
            Commercial Centre
          </h1>
          <p className="text-text-muted text-[13px] mt-1">
            Money flow across the business — quotes → contracts → invoiced → paid.
            {kpis.activeCount > 0 && (
              <> Currently tracking <span className="text-text font-mono">{kpis.activeCount}</span> active project{kpis.activeCount === 1 ? "" : "s"}.</>
            )}
          </p>
        </div>
        {canManage && (
          <div className="flex items-center gap-2 flex-wrap">
            <Link to="/quotes">
              <Button variant="secondary" size="sm" icon={FileText}>Quotes</Button>
            </Link>
            <Link to="/ap">
              <Button variant="secondary" size="sm" icon={Banknote}>AP</Button>
            </Link>
            <Link to="/commercial">
              <Button variant="primary" size="sm" icon={TrendingUp}>Commercial</Button>
            </Link>
          </div>
        )}
      </div>

      {/* Finance portal nav — quick-jump tiles */}
      <div className="mb-6">
        <div className="text-text-muted text-[10px] tracking-wider2 uppercase font-mono mb-2">
          Jump to
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-2">
          <NavTile to="/" label="All hubs" icon={LayoutGrid} />
          <NavTile to="/quotes" label="Quotes" icon={FileText} />
          <NavTile to="/invoices" label="Invoices (AR)" icon={Receipt} />
          <NavTile to="/ap" label="AP" icon={Banknote} />
          <NavTile to="/ap/xero-match" label="Xero rec" icon={CheckCircle2} />
          <NavTile to="/pos" label="Purchase Orders" icon={ClipboardList} />
          <NavTile to="/opex" label="OPEX" icon={TrendingUp} />
          {/* T-508 Phase 4 — Clients moved here from the Project hub; Hours went
              the other way. Clients carries lifetime invoiced and outstanding. */}
          <NavTile to="/clients" label="Clients" icon={Building2} />
          <NavTile to="/commercial" label="Commercial" icon={TrendingUp} />
          <NavTile to="/finance/budget-codes" label="Budget codes" icon={Tag} />   {/* mig 469 */}
        </div>
      </div>

      {/* KPI strip — 6 commercial tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <KPI
          label="Active contract"
          value={fmtMoney(kpis.activeContractValue)}
          icon={ClipboardList}
          sub="value across active"
          accent={kpis.activeContractValue > 0}
        />
        <KPI
          label="Gross margin"
          value={fmtMoney(kpis.totalGrossMargin)}
          icon={TrendingUp}
          sub={`avg ${kpis.avgMarginPct.toFixed(1)}%`}
          accent={kpis.avgMarginPct > 15}
        />
        <KPI
          label="AR outstanding"
          value={fmtMoney(kpis.arOutstanding)}
          icon={Receipt}
          sub="unpaid invoices"
          accent={kpis.arOutstanding > 0}
        />
        <KPI
          label="AP backlog"
          value={fmtMoney(apStats?.total ?? 0)}
          icon={Banknote}
          sub={`${apStats?.count ?? 0} pending`}
          accent={(apStats?.count ?? 0) > 0}
        />
        <KPI
          label="Quote pipeline"
          value={fmtMoney(quoteStats?.total ?? 0)}
          icon={FileText}
          sub={`${quoteStats?.count ?? 0} awaiting`}
        />
        <KPI
          label="Active projects"
          value={kpis.activeCount}
          icon={Building2}
          sub="generating revenue"
        />
      </div>

      {/* Two-column row: Top clients chart + Margin breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Top clients chart — 2/3 wide */}
        <Card className="lg:col-span-2">
          <div className="p-4">
            <div className="flex items-center justify-between gap-3 mb-1">
              <div className="flex items-center gap-2 min-w-0">
                <Building2 size={14} className="text-accent flex-shrink-0"/>
                <h3 className="text-[13px] font-semibold text-text">Top clients by contract value</h3>
                <span className="text-[10px] font-mono text-text-dim hidden sm:inline">· margin overlay</span>
              </div>
              <Link to="/clients" className="text-[11px] text-accent hover:underline">All clients →</Link>
            </div>
            {topClients.length === 0 ? (
              <div className="py-12 text-center text-[12px] text-text-muted italic">
                No client revenue yet — create projects with contract values to see this chart.
              </div>
            ) : (
              <div className="mt-3">
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={topClients} layout="vertical" margin={{ top: 5, right: 30, left: 80, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" horizontal={false}/>
                      <XAxis type="number" tickFormatter={fmtMoney} tick={{ fill: "rgb(150,150,160)", fontSize: 10 }} />
                      <YAxis type="category" dataKey="name" tick={{ fill: "rgb(180,180,190)", fontSize: 11 }} width={80} />
                      <Tooltip
                        contentStyle={{
                          background: "#14161a",
                          border: "1px solid #2a2e35",
                          borderRadius: 6,
                          fontSize: 12,
                          padding: "6px 10px",
                        }}
                        itemStyle={{ color: "#f5f5f5", fontSize: 12 }}
                        labelStyle={{ color: "#9aa0a6", fontSize: 11, marginBottom: 2 }}
                        formatter={(value: any, name: string) => {
                          if (name === "contract") return [fmtMoney(value as number), "Contract value"];
                          if (name === "margin")   return [fmtMoney(value as number), "Gross margin"];
                          return [value, name];
                        }}
                      />
                      <Bar dataKey="contract" fill="#F97316" radius={[0, 4, 4, 0]}>
                        {topClients.map((_, i) => <Cell key={i} fill="#F97316" />)}
                      </Bar>
                      <Bar dataKey="margin" fill="#10B981" radius={[0, 4, 4, 0]}>
                        {topClients.map((_, i) => <Cell key={i} fill="#10B981" />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-2 flex items-center gap-4 text-[10px] text-text-muted">
                  <div className="inline-flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm" style={{ background: "#F97316" }}/>
                    Contract value
                  </div>
                  <div className="inline-flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm" style={{ background: "#10B981" }}/>
                    Gross margin
                  </div>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* T-495b — was four counts with no names. "1 underwater" is only useful
            if it says WHICH one, so this lists the actual projects worst-first
            with the money attached, and clicks through to the job. */}
        <Card className="lg:col-span-1">
          <div className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp size={14} className="text-accent flex-shrink-0"/>
              <h3 className="text-[13px] font-semibold text-text">Margin by project</h3>
            </div>
            <p className="text-[10px] text-text-dim mb-3">active jobs, worst first</p>
            <MarginByProjectList projects={projects} />
          </div>
        </Card>
      </div>

      {/* T-495 — the money row. Sits directly under the headline numbers because
          "how much" without "how late" is the part that was missing. */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ArAgeingWidget />
        <ApDueWidget />
        <XeroActivityWidget />
      </div>

      {/* Bottom row: Recent activity (quotes + invoices + AP) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <div className="p-4">
            <div className="flex items-center justify-between gap-3 mb-1">
              <div className="flex items-center gap-2">
                <FileText size={14} className="text-accent"/>
                <h3 className="text-[13px] font-semibold text-text">Recent quotes</h3>
              </div>
              <Link to="/quotes" className="text-[11px] text-accent hover:underline">All quotes →</Link>
            </div>
            <RecentQuotesList />
          </div>
        </Card>

        <Card>
          <div className="p-4">
            <div className="flex items-center justify-between gap-3 mb-1">
              <div className="flex items-center gap-2">
                <Banknote size={14} className="text-accent"/>
                <h3 className="text-[13px] font-semibold text-text">Recent AP invoices</h3>
              </div>
              <Link to="/ap" className="text-[11px] text-accent hover:underline">AP queue →</Link>
            </div>
            <RecentApList />
          </div>
        </Card>
      </div>
    </div>
  );
}

// =============================================================================
// T-495 — the money widgets.
//
// The hub had six headline numbers and no sense of WHEN. $80k outstanding reads
// the same whether it is all current or all ninety days late — and on 09/08 it
// was 77% overdue, which the old tile could not show. These three answer the
// questions a director actually asks: who is late paying us, what do we owe and
// when, and is the Xero push working.
// =============================================================================

const AGE_BUCKETS = [
  { key: "current", label: "Current",  colour: "#10B981", from: -99999, to: 0 },
  { key: "b30",     label: "1–30 days", colour: "#FBBF24", from: 1,     to: 30 },
  { key: "b60",     label: "31–60",     colour: "#F97316", from: 31,    to: 60 },
  { key: "b90",     label: "61–90",     colour: "#EF4444", from: 61,    to: 90 },
  { key: "b90p",    label: "90+",       colour: "#B91C1C", from: 91,    to: 99999 },
];

function daysPast(due: string | null): number {
  if (!due) return 0;
  const ms = Date.now() - new Date(due + "T00:00:00").getTime();
  return Math.floor(ms / 86400000);
}

// -----------------------------------------------------------------------------
// AR ageing — who owes us, and how late.
// -----------------------------------------------------------------------------
function ArAgeingWidget() {
  const { data, isLoading } = useQuery({
    queryKey: ["finance_hub_ar_ageing"],
    queryFn: async () => {
      const { data: rows } = await supabase
        .from("client_invoices")
        .select("invoice_id, invoice_number, total_incl_gst, amount_paid, due_date, status, client:client_id(legal_name)")
        .in("status", ["sent", "part_paid", "overdue"]);

      const buckets = Object.fromEntries(AGE_BUCKETS.map(b => [b.key, 0])) as Record<string, number>;
      const byClient = new Map<string, number>();
      let total = 0, overdue = 0;
      let worst: { name: string; amount: number; days: number } | null = null;

      for (const r of (rows ?? []) as any[]) {
        const owing = Number(r.total_incl_gst ?? 0) - Number(r.amount_paid ?? 0);
        if (owing <= 0.005) continue;
        const d = daysPast(r.due_date);
        const b = AGE_BUCKETS.find(x => d >= x.from && d <= x.to) ?? AGE_BUCKETS[0];
        buckets[b.key] += owing;
        total += owing;
        if (d > 0) overdue += owing;
        const name = r.client?.legal_name ?? "Unknown client";
        byClient.set(name, (byClient.get(name) ?? 0) + owing);
        if (d > 0 && (!worst || owing > worst.amount)) worst = { name, amount: owing, days: d };
      }

      return {
        chart: AGE_BUCKETS.map(b => ({ name: b.label, value: Math.round(buckets[b.key]), colour: b.colour })),
        total, overdue, worst,
        topClients: Array.from(byClient.entries()).sort((a, b) => b[1] - a[1]).slice(0, 3),
      };
    },
  });

  const overduePct = data && data.total > 0 ? (data.overdue / data.total) * 100 : 0;

  return (
    <Card>
      <div className="p-4">
        <div className="flex items-center justify-between gap-3 mb-1">
          <div className="flex items-center gap-2">
            <Receipt size={14} className="text-accent"/>
            <h3 className="text-[13px] font-semibold text-text">AR ageing — who owes us</h3>
          </div>
          <Link to="/invoices" className="text-[11px] text-accent hover:underline">Invoices →</Link>
        </div>
        <p className="text-[10px] text-text-dim mb-3">unpaid client invoices by how late they are</p>

        {isLoading ? (
          <div className="py-10 text-center text-[12px] text-text-muted italic">Loading…</div>
        ) : !data || data.total <= 0 ? (
          <div className="py-10 text-center text-[12px] text-text-muted italic">Nothing outstanding. Rare and good.</div>
        ) : (
          <>
            <div className="flex items-baseline gap-3 flex-wrap">
              <span className="text-2xl font-semibold text-text tracking-tight">{fmtMoney(data.total)}</span>
              <span className={`text-[12px] font-medium ${overduePct >= 50 ? "text-danger" : overduePct > 0 ? "text-warn" : "text-ok"}`}>
                {fmtMoney(data.overdue)} overdue ({overduePct.toFixed(0)}%)
              </span>
            </div>

            <div className="h-[150px] mt-3 -ml-2">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.chart} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ffffff12" vertical={false}/>
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#9aa0a6" }} axisLine={false} tickLine={false}/>
                  <YAxis tickFormatter={(v) => fmtMoney(v)} tick={{ fontSize: 10, fill: "#9aa0a6" }} axisLine={false} tickLine={false} width={48}/>
                  <Tooltip
                    cursor={{ fill: "#ffffff08" }}
                    contentStyle={{ background: "#14161a", border: "1px solid #2a2e35", borderRadius: 6, fontSize: 12, padding: "6px 10px" }}
                    itemStyle={{ color: "#f5f5f5", fontSize: 12 }}
                    labelStyle={{ color: "#9aa0a6", fontSize: 11, marginBottom: 2 }}
                    formatter={(v: any) => [fmtMoney(Number(v)), "Outstanding"]}/>
                  <Bar dataKey="value" radius={[3, 3, 0, 0]}>
                    {data.chart.map((c, i) => <Cell key={i} fill={c.colour}/>)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {data.worst && (
              <div className="mt-2 flex items-start gap-2 rounded border border-danger/25 bg-danger/8 px-2.5 py-1.5 text-[11px] text-danger">
                <AlertTriangle size={12} className="mt-0.5 shrink-0"/>
                <span>Largest overdue: <strong>{data.worst.name}</strong> — {fmtMoney(data.worst.amount)}, {data.worst.days} days past due.</span>
              </div>
            )}

            <div className="mt-2 space-y-1">
              {data.topClients.map(([name, amt]) => (
                <div key={name} className="flex items-center justify-between gap-2 text-[11px]">
                  <span className="text-text-muted truncate">{name}</span>
                  <span className="text-text font-mono shrink-0">{fmtMoney(amt)}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// AP due — what we owe, and when it has to go out.
// -----------------------------------------------------------------------------
function ApDueWidget() {
  const { data, isLoading } = useQuery({
    queryKey: ["finance_hub_ap_due"],
    queryFn: async () => {
      const { data: rows } = await supabase
        .from("ap_invoices")
        .select("ap_invoice_id, supplier_name, amount_inc_gst, due_date, status, xero_entry_type")
        .in("status", ["approved", "entered_in_xero"])
        .is("paid_date", null);

      const g = { overdue: 0, week: 0, month: 0, later: 0, undated: 0 };
      let total = 0;
      const soon: any[] = [];
      for (const r of (rows ?? []) as any[]) {
        if (r.xero_entry_type === "spend_money") continue;   // already paid on the card
        const amt = Number(r.amount_inc_gst ?? 0);
        total += amt;
        if (!r.due_date) { g.undated += amt; continue; }
        const d = daysPast(r.due_date);
        if (d > 0)        { g.overdue += amt; soon.push({ ...r, d }); }
        else if (d >= -7) { g.week   += amt; soon.push({ ...r, d }); }
        else if (d >= -30) g.month  += amt;
        else               g.later  += amt;
      }
      const chart = [
        { name: "Overdue",   value: Math.round(g.overdue), colour: "#EF4444" },
        { name: "≤ 7 days",  value: Math.round(g.week),    colour: "#F97316" },
        { name: "8–30 days", value: Math.round(g.month),   colour: "#FBBF24" },
        { name: "Later",     value: Math.round(g.later),   colour: "#10B981" },
        { name: "No date",   value: Math.round(g.undated), colour: "#6B7280" },
      ].filter(c => c.value > 0);

      soon.sort((a, b) => b.d - a.d);
      return { chart, total, overdue: g.overdue, week: g.week, soon: soon.slice(0, 4) };
    },
  });

  return (
    <Card>
      <div className="p-4">
        <div className="flex items-center justify-between gap-3 mb-1">
          <div className="flex items-center gap-2">
            <Banknote size={14} className="text-accent"/>
            <h3 className="text-[13px] font-semibold text-text">AP due — what we owe</h3>
          </div>
          <Link to="/ap/bills" className="text-[11px] text-accent hover:underline">Bills to pay →</Link>
        </div>
        <p className="text-[10px] text-text-dim mb-3">approved supplier bills awaiting payment</p>

        {isLoading ? (
          <div className="py-10 text-center text-[12px] text-text-muted italic">Loading…</div>
        ) : !data || data.total <= 0 ? (
          <div className="py-10 text-center text-[12px] text-text-muted italic">Nothing owing.</div>
        ) : (
          <>
            <div className="flex items-baseline gap-3 flex-wrap">
              <span className="text-2xl font-semibold text-text tracking-tight">{fmtMoney(data.total)}</span>
              {data.overdue > 0 && <span className="text-[12px] font-medium text-danger">{fmtMoney(data.overdue)} overdue</span>}
              {data.overdue === 0 && data.week > 0 && <span className="text-[12px] font-medium text-warn">{fmtMoney(data.week)} due this week</span>}
            </div>

            <div className="h-[150px] mt-3">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data.chart} dataKey="value" nameKey="name"
                    cx="50%" cy="50%" innerRadius={38} outerRadius={62} paddingAngle={2} stroke="none">
                    {data.chart.map((c, i) => <Cell key={i} fill={c.colour}/>)}
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: "#14161a", border: "1px solid #2a2e35", borderRadius: 6, fontSize: 12, padding: "6px 10px" }}
                    itemStyle={{ color: "#f5f5f5", fontSize: 12 }}
                    labelStyle={{ color: "#9aa0a6", fontSize: 11, marginBottom: 2 }}
                    formatter={(v: any, n: any) => [fmtMoney(Number(v)), n]}/>
                  <Legend verticalAlign="middle" align="right" layout="vertical"
                    iconType="circle" iconSize={7}
                    formatter={(v: any) => <span style={{ fontSize: 10, color: "#9aa0a6" }}>{v}</span>}/>
                </PieChart>
              </ResponsiveContainer>
            </div>

            {data.soon.length > 0 && (
              <div className="mt-1 space-y-1">
                {data.soon.map((r: any) => (
                  <div key={r.ap_invoice_id} className="flex items-center justify-between gap-2 text-[11px]">
                    <span className="text-text-muted truncate">{r.supplier_name ?? "—"}</span>
                    <span className={`shrink-0 font-mono ${r.d > 0 ? "text-danger" : "text-warn"}`}>
                      {fmtMoney(Number(r.amount_inc_gst))} · {r.d > 0 ? `${r.d}d late` : `${Math.abs(r.d)}d`}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Xero push activity — is the integration actually working?
// -----------------------------------------------------------------------------
function XeroActivityWidget() {
  const { data, isLoading } = useQuery({
    queryKey: ["finance_hub_xero_activity"],
    queryFn: async () => {
      const { data: rows } = await supabase
        .from("xero_push_log")
        .select("push_id, created_at, entry_type, outcome, xero_number, attachment_outcome, error_detail, ap_invoice_id")
        .order("created_at", { ascending: false })
        .limit(40);
      const list = (rows ?? []) as any[];
      const ok      = list.filter(r => r.outcome === "ok").length;
      const blocked = list.filter(r => r.outcome === "blocked").length;
      const errored = list.filter(r => r.outcome === "error").length;
      const noDoc   = list.filter(r => r.outcome === "ok" && r.attachment_outcome !== "ok").length;
      return { recent: list.slice(0, 6), ok, blocked, errored, noDoc, total: list.length };
    },
  });

  return (
    <Card>
      <div className="p-4">
        <div className="flex items-center justify-between gap-3 mb-1">
          <div className="flex items-center gap-2">
            <Send size={14} className="text-accent"/>
            <h3 className="text-[13px] font-semibold text-text">Xero push activity</h3>
          </div>
          <Link to="/ap/xero" className="text-[11px] text-accent hover:underline">Push queue →</Link>
        </div>
        <p className="text-[10px] text-text-dim mb-3">last {data?.total ?? 0} attempts</p>

        {isLoading ? (
          <div className="py-10 text-center text-[12px] text-text-muted italic">Loading…</div>
        ) : !data || data.total === 0 ? (
          <div className="py-10 text-center text-[12px] text-text-muted italic">Nothing pushed yet.</div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2">
              <StatChip label="Posted"  value={data.ok}      tone="ok"     icon={CheckCircle2}/>
              <StatChip label="Blocked" value={data.blocked} tone="warn"   icon={Ban}/>
              <StatChip label="Errors"  value={data.errored} tone="danger" icon={AlertTriangle}/>
            </div>

            {data.noDoc > 0 && (
              <div className="mt-2 flex items-start gap-2 rounded border border-warn/25 bg-warn/8 px-2.5 py-1.5 text-[11px] text-warn">
                <Paperclip size={12} className="mt-0.5 shrink-0"/>
                <span>{data.noDoc} in Xero without the receipt attached — attach by hand.</span>
              </div>
            )}

            <div className="mt-3 space-y-1.5">
              {data.recent.map((r: any) => (
                <div key={r.push_id} className="flex items-center gap-2 text-[11px]">
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                    r.outcome === "ok" ? "bg-ok" : r.outcome === "blocked" ? "bg-warn" : "bg-danger"}`}/>
                  <span className="text-text truncate">
                    {r.outcome === "ok"
                      ? <>{r.entry_type === "spend_money" ? "Spend" : "Bill"} {r.xero_number ?? ""}</>
                      : <span className="text-text-muted">{(r.error_detail ?? r.outcome).split(",")[0]}</span>}
                  </span>
                  {r.outcome === "ok" && r.attachment_outcome !== "ok" && (
                    <Paperclip size={10} className="text-warn shrink-0"/>
                  )}
                  <span className="ml-auto shrink-0 text-text-dim">
                    {formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </Card>
  );
}

function StatChip({ label, value, tone, icon: Icon }: {
  label: string; value: number; tone: "ok" | "warn" | "danger"; icon: any;
}) {
  const cls = tone === "ok" ? "text-ok border-ok/25 bg-ok/8"
            : tone === "warn" ? "text-warn border-warn/25 bg-warn/8"
            : "text-danger border-danger/25 bg-danger/8";
  return (
    <div className={`rounded border px-2 py-1.5 ${value === 0 ? "border-line bg-ink text-text-muted" : cls}`}>
      <div className="flex items-center gap-1.5">
        <Icon size={11} className="shrink-0"/>
        <span className="text-[16px] font-semibold leading-none">{value}</span>
      </div>
      <div className="text-[10px] mt-0.5 opacity-80">{label}</div>
    </div>
  );
}

// -----------------------------------------------------------------------------
function NavTile({ to, label, icon: Icon }: { to: string; label: string; icon: any }) {
  return (
    <Link
      to={to}
      className="group inline-flex items-center gap-2 px-3 py-2.5 rounded bg-ink-raised border border-line text-accent hover:border-accent hover:bg-ink-hover transition-colors"
    >
      <Icon size={14} className="flex-shrink-0" />
      <span className="text-[12px] font-medium truncate">{label}</span>
    </Link>
  );
}

// -----------------------------------------------------------------------------
// Margin by project. Bar is |margin %| against a 50% reference, so a healthy job
// and a bleeding one are distinguishable at a glance rather than by reading.
function MarginByProjectList({ projects }: { projects: ProjectWithClient[] }) {
  const ACTIVE = ["mobilising", "live", "acceptance_pending"];
  const rows = projects
    .filter(p => ACTIVE.includes(p.status))
    .sort((a, b) => (a.margin_pct ?? 0) - (b.margin_pct ?? 0));

  if (rows.length === 0) {
    return <div className="py-8 text-center text-[12px] text-text-muted italic">No active projects to assess.</div>;
  }

  const bandOf = (pct: number) =>
    pct < 0  ? { colour: "#EF4444", label: "Underwater" }
  : pct < 10 ? { colour: "#F97316", label: "At risk" }
  : pct <= 20 ? { colour: "#FBBF24", label: "OK" }
  :             { colour: "#10B981", label: "Healthy" };

  const under = rows.filter(p => (p.margin_pct ?? 0) < 0).length;
  const healthy = rows.filter(p => (p.margin_pct ?? 0) > 20).length;

  return (
    <div>
      <div className="flex items-center gap-3 text-[11px] mb-3">
        <span className="inline-flex items-center gap-1 text-ok"><CheckCircle2 size={11}/>{healthy} healthy</span>
        {under > 0 && <span className="inline-flex items-center gap-1 text-danger"><TrendingDown size={11}/>{under} underwater</span>}
        <span className="text-text-dim ml-auto">{rows.length} active</span>
      </div>

      <div className="space-y-2.5">
        {rows.map(p => {
          const pct  = p.margin_pct ?? 0;
          const band = bandOf(pct);
          const width = Math.min(Math.abs(pct), 50) / 50 * 100;
          return (
            <Link key={p.project_id} to={`/projects/${p.project_id}`} className="block group">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-[11px] text-text truncate group-hover:text-accent transition-colors">
                  <span className="font-mono text-text-muted">{p.project_code}</span> {p.project_name}
                </span>
                <span className="text-[11px] font-mono shrink-0" style={{ color: band.colour }}>
                  {pct.toFixed(1)}%
                </span>
              </div>
              <div className="mt-1 flex items-center gap-2">
                <div className="h-1.5 flex-1 rounded-full bg-ink overflow-hidden">
                  <div className="h-full rounded-full transition-all"
                    style={{ width: `${width}%`, background: band.colour }}/>
                </div>
                <span className="text-[10px] text-text-dim font-mono shrink-0 w-14 text-right">
                  {fmtMoney(p.contract_value)}
                </span>
              </div>
            </Link>
          );
        })}
      </div>

      <div className="pt-3 mt-3 border-t border-line">
        <Link to="/commercial" className="text-[11px] text-accent hover:underline inline-flex items-center gap-1">
          Open Commercial dashboard <ArrowUpRight size={10}/>
        </Link>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
function RecentQuotesList() {
  const { data: quotes = [] } = useQuery({
    queryKey: ["finance_hub_recent_quotes"],
    queryFn: async () => {
      // Pull the site context off the quote (or its linked bid as a
      // fallback). quotes.site_id is set directly when the quote is built
      // from a bid; older quotes only have bid_id, so we read both and
      // prefer whichever has a hit.
      const { data } = await supabase
        .from("quotes")
        .select(`
          quote_id, quote_number, status, total_incl_gst, created_at, project_id,
          quote_site:site_id    (site_code, site_name, suburb),
          bids:bid_id           (
            bid_id,
            bid_site:site_id    (site_code, site_name, suburb)
          )
        `)
        .order("created_at", { ascending: false })
        .limit(5);
      return data ?? [];
    },
  });

  if (quotes.length === 0) {
    return <div className="py-6 text-center text-[12px] text-text-muted italic">No quotes yet.</div>;
  }

  return (
    <div className="space-y-2 mt-3">
      {quotes.map((q: any) => {
        const site = q.quote_site ?? q.bids?.bid_site ?? null;
        const siteLabel = site
          ? [site.site_name, site.suburb].filter(Boolean).join(" · ")
          : null;
        return (
          <Link key={q.quote_id} to={`/quotes/${q.quote_id}`} className="block">
            <div className="p-2.5 rounded bg-ink border border-line hover:border-accent transition-colors">
              <div className="flex items-center justify-between gap-2">
                <div className="text-[12px] font-mono text-text truncate">{q.quote_number}</div>
                <div className="text-[12px] font-semibold text-text">{fmtMoney(q.total_incl_gst)}</div>
              </div>
              {siteLabel && (
                <div className="text-[11px] text-text mt-0.5 truncate inline-flex items-center gap-1">
                  {site?.site_code && <span className="font-mono text-[10px] text-text-muted">{site.site_code}</span>}
                  <span className="truncate">{siteLabel}</span>
                </div>
              )}
              <div className="text-[10px] text-text-muted mt-0.5 flex items-center gap-2">
                <span className="capitalize">{q.status?.replace("_", " ")}</span>
                <span className="text-text-dim">·</span>
                <span>{formatDistanceToNow(parseISO(q.created_at), { addSuffix: true })}</span>
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  );
}

// -----------------------------------------------------------------------------
function RecentApList() {
  const { data: invoices = [] } = useQuery({
    queryKey: ["finance_hub_recent_ap"],
    queryFn: async () => {
      const { data } = await supabase
        .from("ap_invoices")
        // T-397 — invoice_number + amount_inc_gst are the real columns
        // (supplier_invoice_number / total_incl_gst never existed → list was empty)
        // T-495 — supplier_name is the column the AP flow actually populates.
        // Only supplier_name_raw was read, so anything without a LINKED supplier
        // record showed as "Unknown supplier" (e.g. MM Electrical after its push).
        .select("ap_invoice_id, invoice_number, status, amount_inc_gst, created_at, supplier_name, supplier_name_raw, supplier:supplier_id(legal_name)")
        .order("created_at", { ascending: false })
        .limit(5);
      return data ?? [];
    },
  });

  if (invoices.length === 0) {
    return <div className="py-6 text-center text-[12px] text-text-muted italic">No AP invoices yet.</div>;
  }

  return (
    <div className="space-y-2 mt-3">
      {invoices.map((inv: any) => (
        <Link key={inv.ap_invoice_id} to="/ap" className="block">
          <div className="p-2.5 rounded bg-ink border border-line hover:border-accent transition-colors">
            <div className="flex items-center justify-between gap-2">
              <div className="text-[12px] text-text truncate">
                {inv.supplier?.legal_name ?? inv.supplier_name ?? inv.supplier_name_raw ?? "Unknown supplier"}
              </div>
              <div className="text-[12px] font-semibold text-text">{fmtMoney(inv.amount_inc_gst)}</div>
            </div>
            <div className="text-[10px] text-text-muted mt-0.5 flex items-center gap-2">
              {inv.invoice_number && <span className="font-mono">{inv.invoice_number}</span>}
              {inv.invoice_number && <span className="text-text-dim">·</span>}
              <span className="capitalize">{inv.status?.replace("_", " ")}</span>
              <span className="text-text-dim">·</span>
              <span>{formatDistanceToNow(parseISO(inv.created_at), { addSuffix: true })}</span>
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}
